import React from 'react';
import styled from 'styled-components';
import { useDisplayPrice } from '@airasia-common/libraries/utilities/formatters';
import {
  listFormat,
  convertTagToTranslationID,
} from '@airasia-common/libraries/i18n/LocaleHelper';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import PhoenixTheme from '@airasia/phoenix-components/components/Theme';
import theme from '@airasia-common/libraries/theming/theme';
import loadable from '@loadable/component';
import useWindowSize from '../../utils/windowSize';
import Row from '../Container/Row';
import Wrapper from '../Wrapper';
import Card from '../Card/Card';
import { SingleContentCard } from '../Card/TemplateCard';
import Location from '../Location';
import PaymentDiscount from '../PaymentDiscount';
import Text from '../Text/Text';
import Button from '../Button';
import HotelRoomPrice from '../HotelRoomPrice';
import hotelCardPropTypes from './HotelCard.propTypes';
const BigPointToBeEarned = loadable(() =>
  import(
    /* webpackChunkName:"BigPointToBeEarned" */ '@airasia/phoenix-components/components/BigPointToBeEarned'
  )
);
const Popover = loadable(() =>
  import(
    /* webpackChunkName:"Popover" */ '@airasia/phoenix-components/components/Popover'
  )
);
const GuestRating = loadable(() =>
  import(/* webpackChunkName:"GuestRating" */ '../GuestRating')
);
const TripAdvisor = loadable(() =>
  import(/* webpackChunkName:"TripAdvisor" */ '../TripAdvisor')
);
const StarRating = loadable(() =>
  import(/* webpackChunkName:"StarRating" */ '../StarRating')
);

const BigPointToBeEarnedContainer = props => <Wrapper {...props} />;

const HighlightBar = styled(Wrapper)`
  width: 100%;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 4px 4px 0 0;
  display: block;
  box-sizing: border-box;
  padding: 8px 16px;
  min-height: 40px;
  color: ${({ theme }) => theme.colors.primaryWhite};
  font-size: 14px;
  line-height: 24px;
`;

const SubtitleTextHeader = styled(Card.SubtitleText)`
  white-space: nowrap;
  ${({ color, backgroundColor }) =>
    color &&
    backgroundColor &&
    `
      color: ${color};
      background-color: ${backgroundColor};
    `}
`;

SubtitleTextHeader.displayName = 'SubtitleTextHeader';

SubtitleTextHeader.defaultProps = {
  fontSize: '12px',
  fontWeight: '500',
  color: 'tertiaryGreen',
  padding: '4px',
  borderRadius: '4px',
  backgroundColor: 'tertiaryGreenLighter',
  lineHeight: '18px',
};

const PopoverContent = styled(Text)`
  color: ${({ theme }) => theme.colors.greyLighter};
`;

PopoverContent.displayName = 'PopoverContent';

PopoverContent.defaultProps = {
  fontSize: '12px',
  fontWeight: 'normal',
  lineHeight: '125%',
};

const HotelCardHeader = ({
  name,
  aboveNameLabels,
  locale,
  payAtHotelLabel,
  flyerDiscountLabel,
  flyerDiscountDescriptionLabel,
  payLaterLabel,
}) => {
  const convertedAboveNameLabels = aboveNameLabels?.map(label =>
    convertTagToTranslationID(label)
  );
  const aboveNameLabel = listFormat(convertedAboveNameLabels, locale);
  const { isMobileView } = useWindowSize();
  return (
    <>
      <Wrapper
        display={'flex'}
        flexDirection={'row'}
        flexWrap={['wrap', 'initial']}
      >
        {payAtHotelLabel && (
          <Row>
            <SubtitleTextHeader>{payAtHotelLabel}</SubtitleTextHeader>
          </Row>
        )}
        {payLaterLabel && (
          <Row>
            <SubtitleTextHeader marginLeft={payAtHotelLabel ? '4px' : 0}>
              {payLaterLabel}
            </SubtitleTextHeader>
          </Row>
        )}
        {aboveNameLabel !== '' && (
          <Row>
            <SubtitleTextHeader
              className="above-name-text"
              marginLeft={payAtHotelLabel || payLaterLabel ? '4px' : 0}
            >
              {aboveNameLabel}
            </SubtitleTextHeader>
          </Row>
        )}
        {flyerDiscountLabel &&
          (!isMobileView ? (
            <Row>
              <Wrapper
                marginLeft={
                  payAtHotelLabel || payLaterLabel || aboveNameLabel !== ''
                    ? '4px'
                    : 0
                }
              >
                <Popover
                  accessibile
                  accessible
                  content={
                    <PopoverContent>
                      {flyerDiscountDescriptionLabel}
                    </PopoverContent>
                  }
                  position="top"
                  theme="AIRASIA_CLASSIC"
                  trigger="hover"
                  width={200}
                  zIndex={1}
                  background={theme.colors.darkBlack}
                  borderRadius={4}
                  left={'50%'}
                  marginLeft={'-112px'}
                  top={'inherit'}
                  bottom={'140%'}
                >
                  <SubtitleTextHeader
                    color={theme.colors.primaryDarker}
                    backgroundColor={theme.colors.primaryLight}
                  >
                    {flyerDiscountLabel}
                  </SubtitleTextHeader>
                </Popover>
              </Wrapper>
            </Row>
          ) : (
            <Row>
              <SubtitleTextHeader
                marginLeft={
                  payAtHotelLabel || payLaterLabel || aboveNameLabel !== ''
                    ? '4px'
                    : 0
                }
                color={theme.colors.primaryDarker}
                backgroundColor={theme.colors.primaryLight}
              >
                {flyerDiscountLabel}
              </SubtitleTextHeader>
            </Row>
          ))}
      </Wrapper>
      <Card.TitleText
        className="hotel-name"
        marginTop={
          payAtHotelLabel ||
          payLaterLabel ||
          aboveNameLabel !== '' ||
          flyerDiscountLabel
            ? ['7px', '10px']
            : 0
        }
      >
        {name}
      </Card.TitleText>
    </>
  );
};

HotelCardHeader.propTypes = hotelCardPropTypes;

const HotelCardLeftContent = ({
  starRating,
  location,
  guestRating,
  guestRatingLabel,
  guestRatingCount,
  tripAdvisorData,
  paymentDiscountType,
  paymentDiscountDescription,
  override,
}) => {
  const { isMobileView } = useWindowSize();
  return (
    <>
      {starRating > 0 && (
        <Row>
          <StarRating rating={starRating} theme={theme} />
        </Row>
      )}

      {isMobileView && override ? null : (
        <>
          {location && <Location width="100%" location={location} />}
          {tripAdvisorData ? (
            <TripAdvisor
              width="10%"
              ratingImageUrl={tripAdvisorData.ratingImageUrl}
              reviewCount={tripAdvisorData.reviewCount}
              rankingString={tripAdvisorData.rankingString}
              reviewUrl={tripAdvisorData.reviewUrl}
              override={override}
            />
          ) : (
            guestRating > 0 &&
            guestRatingCount > 0 && (
              <GuestRating
                width="100%"
                rating={guestRating}
                ratingLabel={guestRatingLabel}
                reviewCount={guestRatingCount}
              />
            )
          )}
        </>
      )}

      {!isMobileView && paymentDiscountType && paymentDiscountDescription && (
        <PaymentDiscount
          width="100%"
          type={paymentDiscountType}
          description={paymentDiscountDescription}
          isMobileView={isMobileView}
        />
      )}
    </>
  );
};

HotelCardLeftContent.propTypes = hotelCardPropTypes;

const HotelCardRightContent = ({
  baseRate,
  strikethroughPrice,
  guestCount,
  guestHintText,
  priceHintText,
  discount,
  bigPoint,
  discountType,
  enableTotalGuestPrice,
  isEnableNewBrandLogo,
  hideBigPoints,
  showSelectRoomCTA,
  onSelectRoomClicked,
  perPersonBaseRate = {},
  perPersonStrikethroughPrice = {},
  applyBigpoints,
  baseRateAfterBigPointsDeduction,
  isLoggedIn,
  bigMembership,
  isSnap,
  override,
  move,
}) => {
  const { isMobileView } = useWindowSize();
  const [roundBaseValue, roundStrikethroughPrice] = useDisplayPrice(
    baseRate,
    strikethroughPrice,
    applyBigpoints,
    baseRateAfterBigPointsDeduction,
    bigMembership
  );
  const [
    roundPerPersonBaseValue,
    roundPerPersonStrikethroughPrice,
  ] = useDisplayPrice(perPersonBaseRate, perPersonStrikethroughPrice);

  const bigPointsIconBgFill = isEnableNewBrandLogo
    ? {
        bigPointsIconBgFill: '#F99D1C',
      }
    : {};

  const hideInfo = isMobileView && override;
  const props = {
    baseRate,
    strikethroughPrice,
    guestCount,
    guestHintText,
    priceHintText,
    enableTotalGuestPrice,
    perPersonBaseRate,
    perPersonStrikethroughPrice,
    isSnap,
    roundBaseValue,
    roundStrikethroughPrice,
    roundPerPersonBaseValue,
    roundPerPersonStrikethroughPrice,
    hideInfo,
  };

  return (
    <>
      {hideInfo ? null : (
        <>
          {discount &&
            (discountType === 2 ? (
              <Card.PriceOffTextV2 className="sale-off">
                {discount}
              </Card.PriceOffTextV2>
            ) : (
              <Card.PriceOffText className="sale-off">
                {discount}
              </Card.PriceOffText>
            ))}
        </>
      )}

      {baseRate && baseRate.value && <HotelRoomPrice props={props} />}

      {showSelectRoomCTA && (
        <Wrapper
          className="select-room-button"
          marginTop={'12px'}
          width={['min(140px, 100%)', '164px']}
        >
          <Button
            big
            secondary
            block
            padding="12px 0"
            onClick={onSelectRoomClicked}
            move
            border={move && '#023C40'}
            borderRadius={move && '32px'}
            hovercolor={move && '#12C25A'}
            backgroundColor={move && '#0CE865'}
          >
            {translate('roomCard.selectRoom')}
          </Button>
        </Wrapper>
      )}
      {!hideBigPoints && bigPoint && (
        <BigPointToBeEarnedContainer mt="9px">
          <PhoenixTheme>
            <BigPointToBeEarned
              template={bigPoint.template}
              pointTemplate={bigPoint.pointTemplate}
              pointValue={bigPoint.pointValue}
              multiplierTemplate={bigPoint.multiplierTemplate}
              multiplierValue={bigPoint.multiplierValue}
              customProperties={bigPointsIconBgFill}
            />
          </PhoenixTheme>
        </BigPointToBeEarnedContainer>
      )}
    </>
  );
};

HotelCardRightContent.propTypes = hotelCardPropTypes;

const HotelCardBottomContent = ({
  paymentDiscountType,
  paymentDiscountDescription,
}) => {
  const { isMobileView } = useWindowSize();
  return (
    <>
      {isMobileView && paymentDiscountType && paymentDiscountDescription && (
        <Row>
          <PaymentDiscount
            width="100%"
            type={paymentDiscountType}
            description={paymentDiscountDescription}
            isMobileView={isMobileView}
          />
        </Row>
      )}
    </>
  );
};

HotelCardBottomContent.propTypes = hotelCardPropTypes;

const ClickableHotelCard = styled(Wrapper)`
  cursor: pointer;
`;

const HotelCard = props => {
  const {
    image,
    images,
    leftPillTexts,
    leftPillType = 1,
    rightPillTexts,
    onClick,
    onLoadThumbnailImage,
    locale,
    highlight,
    focusMessage,
    isShowCarousel,
    isHotel,
    override = false,
    savings,
  } = props;

  const convertedLeftPillTexts = leftPillTexts?.map(label =>
    convertTagToTranslationID(label)
  );
  const convertedRightPillTexts = rightPillTexts?.map(label =>
    convertTagToTranslationID(label)
  );
  const { isMobileView } = useWindowSize();
  const leftPillText =
    isMobileView && override
      ? savings
        ? translate('hotelCard.priceOff', { percent: savings })
        : null
      : !highlight
      ? listFormat(convertedLeftPillTexts, locale)
      : null;
  const rightPillText = listFormat(convertedRightPillTexts, locale);
  let highlightText =
    convertedLeftPillTexts && convertedLeftPillTexts.length
      ? convertedLeftPillTexts[0]
      : null;

  if (focusMessage || focusMessage === '') {
    highlightText = focusMessage;
  }

  return (
    <ClickableHotelCard onClick={onClick}>
      {highlight && (
        <HighlightBar className="highlight-bar">{highlightText}</HighlightBar>
      )}
      <SingleContentCard
        thumbnailImage={image}
        images={images}
        isShowCarousel={isShowCarousel}
        onLoadThumbnailImage={onLoadThumbnailImage}
        leftPillText={leftPillText}
        leftPillType={leftPillType}
        rightPillText={rightPillText}
        headerComponent={<HotelCardHeader {...props} />}
        leftContentComponent={
          <HotelCardLeftContent {...props} override={override} />
        }
        rightContentComponent={
          <HotelCardRightContent {...props} override={override} />
        }
        bottomContentComponent={<HotelCardBottomContent {...props} />}
        highlight={highlight}
        focusable
        isHotel={isHotel}
        override={override}
      />
    </ClickableHotelCard>
  );
};

HotelCard.propTypes = hotelCardPropTypes;

HotelCard.defaultProps = {
  enableTotalGuestPrice: false,
  hideBigPoints: false,
  showSelectRoomCTA: false,
  onSelectRoomClicked: () => {},
};

export default HotelCard;
